"use client";

import { ImageProps, ThemeVariant } from "@/types";
import { TextBlockContent, TextBlockContentProps } from "./Text";
import { Arrow } from "../ui/Arrow";
import { Image } from "@/components/ui/Image";
import React, { useState } from "react";
import { ChipFilterGroup } from "../ui/ChipFilterGroup";
import { useTranslations } from "@/lib/i18n/useTranslations";

export interface Employee {
  name: string;
  jobTitle: string;
  image: ImageProps;
}

export interface Department {
  id: string;
  name: string;
  description: string;
  persons: Employee[];
}

export interface EmployeeBlockProps {
  departments: Department[];
  variant?: ThemeVariant;
  className?: string;
}

export const EmployeeBlock: React.FC<EmployeeBlockProps> = ({
  departments,
  variant,
  className,
}) => {
  const t = useTranslations();

  const [randomNumber] = useState(() => Math.random());
  const [seenDepartments, setSeenDepartments] = useState(departments);
  const [department, setDepartment] = useState<Department | null>(
    () => departments[Math.floor(randomNumber * departments.length)]
  );

  if (departments.length !== seenDepartments.length) {
    setSeenDepartments(departments);
    const departmentIndex = Math.floor(randomNumber * seenDepartments.length);
    setDepartment(
      seenDepartments.length ? seenDepartments[departmentIndex] : null
    );
  }

  if (!department) return;
  return (
    <div className={`container py-lg ${className}`}>
      <ChipFilterGroup
        key={seenDepartments.map((department) => department.id).join(",")}
        variant={variant}
        label={t.common.selectDepartment}
        tags={departments.map((department) => ({
          label: department.name,
          id: department.id,
        }))}
        searchParamName="unit-filter"
        canSelectNone={false}
        defaultActiveTags={[department.id]}
        onChange={(tags) => {
          setDepartment(
            seenDepartments.find((department) => department.id === tags[0]) ??
              null
          );
        }}
      />
      {/* Every department is rendered so crawlers (and no-JS visitors) get the
          full list; the filter only toggles which one is displayed. */}
      {seenDepartments.map((dept) => (
        <EmployeeGrid
          key={dept.id}
          hidden={dept.id !== department.id}
          variant={variant}
          topline={t.common.department}
          headline={dept.name}
          text={dept.description}
          employees={dept.persons}
          className="mt-xl"
        />
      ))}
    </div>
  );
};

interface EmployeeGridProps extends Pick<
  TextBlockContentProps,
  | "topline"
  | "headline"
  | "subline"
  | "text"
  | "headingTag"
  | "headingStyle"
  | "button"
> {
  employees: Employee[];
  variant?: ThemeVariant;
  /** Kept in the DOM, just not displayed — see the filter above. */
  hidden?: boolean;
  className?: string;
}

const EmployeeGrid: React.FC<EmployeeGridProps> = ({
  topline,
  headline,
  subline,
  text,
  headingTag = "h2",
  headingStyle = "h2",
  button,
  employees,
  variant = "light",
  hidden = false,
  className = "",
}) => {
  return (
    <div
      // `display` has to carry this: the `hidden` attribute loses to the
      // `grid` utility, author styles beating the UA stylesheet.
      className={`col-span-full ${hidden ? "hidden" : "grid"} grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 ${className}`}
    >
      <div className="invisible md:visible flex flex-row justify-center items-center">
        <Arrow
          variant={"large"}
          direction={"down"}
          color={variant === "dark" ? "purple" : "yellow"}
        />
      </div>
      <div className="col-span-full md:col-start-2 md:-col-end-1 mb-10">
        <TextBlockContent
          topline={topline}
          headline={headline}
          subline={subline}
          text={text}
          headingTag={headingTag}
          headingStyle={headingStyle}
          button={button}
          variant={variant}
        />
      </div>
      {employees.map((employee, index) => (
        <EmployeePreview key={index} {...employee} />
      ))}
    </div>
  );
};

export const EmployeePreview: React.FC<Employee> = ({
  name,
  jobTitle,
  image,
}) => {
  return (
    <div className="group relative aspect-square rounded-md overflow-hidden">
      <Image
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        sizes={image.sizes}
        priority={image.priority}
        rounded="none"
        className="size-full"
      />
      <div className="absolute inset-0 bg-purple-dark/80 mix-blend-multiply opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      <div className="absolute inset-0 p-4 flex flex-col justify-center items-center text-center text-white opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300">
        <span className="text-2xl pb-1">{name}</span>
        <span className="text-sm uppercase">{jobTitle}</span>
      </div>
    </div>
  );
};
